import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Navbar,
  NavBody,
  NavItems,
  MobileNav,
  NavbarLogo,
  NavbarButton,
  MobileNavHeader,
  MobileNavToggle,
  MobileNavMenu,
} from "@/components/ui/resizable-navbar";
import { NavDropdown } from "@/components/ui/nav-dropdown";
import { FileText, Mail } from "lucide-react";
import ayraLogoDark from "@/assets/ayra-logo-dark.png";
import ayraLogoLight from "@/assets/ayra-logo-light.png";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    {
      name: "Agency Partners",
      link: "/agency-partners",
    },
    {
      name: "About",
      link: "/about",
    },
    {
      name: "Careers",
      link: "/careers",
    },
  ];

  const dropdownItems = [
    {
      name: "DOCS",
      link: "https://docs.ayra.cx/docs/get-started/welcome",
      icon: <FileText className="h-4 w-4" />,
    },
    {
      name: "Blog",
      link: "/blog",
      icon: <FileText className="h-4 w-4" />,
    },
  ];

  return (
    <div className="relative w-full">
      <Navbar>
        {/* Desktop Navigation */}
        <NavBody>
          <NavbarLogo logoDark={ayraLogoDark} logoLight={ayraLogoLight} />
          <NavItems items={navItems}>
            <NavDropdown label="Resources" items={dropdownItems} />
          </NavItems>
          <div className="flex items-center gap-2 ml-auto relative z-20">
            <NavbarButton variant="green" href="/contact">Contact Us</NavbarButton>
            <NavbarButton variant="dark" href="https://app.ayra.cx/login" as="a" target="_blank" rel="noopener noreferrer">Login</NavbarButton>
          </div>
        </NavBody>

        {/* Mobile Navigation */}
        <MobileNav>
          <MobileNavHeader>
            <NavbarLogo logoDark={ayraLogoDark} logoLight={ayraLogoLight} />
            <MobileNavToggle
              isOpen={isMobileMenuOpen}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            />
          </MobileNavHeader>

          <MobileNavMenu
            isOpen={isMobileMenuOpen}
            onClose={() => setIsMobileMenuOpen(false)}
          >
            {navItems.map((item, idx) => (
              <motion.div
                key={`mobile-link-${idx}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: idx * 0.1 }}
              >
                <Link
                  to={item.link}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="relative text-neutral-600"
                >
                  <motion.span 
                    className="block"
                    whileHover={{ x: 5, transition: { duration: 0.2 } }}
                  >
                    {item.name}
                  </motion.span>
                </Link>
              </motion.div>
            ))}
            <details className="w-full">
              <motion.summary 
                className="flex cursor-pointer list-none items-center justify-between text-neutral-600 dark:text-neutral-300"
                whileHover={{ x: 5 }}
                transition={{ duration: 0.2 }}
              >
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  Resources
                </motion.span>
                <motion.span 
                  className="text-xs"
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  ▼
                </motion.span>
              </motion.summary>
              <div className="mt-2 space-y-2 pl-2">
                {dropdownItems.map((item) =>
                  item.link.startsWith("http") ? (
                    <a
                      key={item.name}
                      href={item.link}
                      onClick={() => setIsMobileMenuOpen(false)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300"
                    >
                      {item.icon}
                      <span>{item.name}</span>
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.link}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 text-neutral-600 dark:text-neutral-300"
                    >
                      {item.icon}
                      <span>{item.name}</span>
                    </Link>
                  ),
                )}
              </div>
            </details>
            <div className="flex items-center justify-end pt-4 border-t border-gray-200">
              <NavbarButton
                onClick={() => setIsMobileMenuOpen(false)}
                variant="dark"
                href="https://app.ayra.cx/login"
                as="a"
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 ml-4"
              >
                Login
              </NavbarButton>
            </div>
          </MobileNavMenu>
        </MobileNav>
      </Navbar>
    </div>
  );
};

export default Header;
